// auth.jsx — Sign in / Sign up / Forgot password / Reset password screens
// Uses window.AuthAPI from db.js. Shares Vital aesthetic with the rest of the app.

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Do not reintroduce destructured short aliases.

// ── First-launch language picker ────────────────────────────────────────────
// Rendered ABOVE every other route on the first cold launch ever, before
// the splash / auth bootstrap touch React state. Two giant buttons; the
// user's choice is persisted via setTweak (so it survives) plus a
// localStorage marker so we never re-prompt. App.jsx gates rendering on
// the marker and short-circuits if already chosen.
function LanguagePickerScreen({ onPick }) {
  return (
    <div className="auth-shell" style={{ justifyContent: 'center' }}>
      <div style={{
        padding: '0 28px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 32,
      }}>
        <div>
          <div className="auth-eyebrow mono">Welcome · 歡迎</div>
          <h1 className="auth-title serif" style={{ fontSize: 32 }}>
            Choose your language.<br/>
            <span style={{ display: 'inline-block', marginTop: 4 }}>選擇語言。</span>
          </h1>
          <p className="auth-sub">
            You can change this anytime in Profile → Preferences.<br/>
            日後可在「我的 → 偏好設定」中更改。
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            type="button"
            className="auth-primary"
            onClick={() => onPick('en')}
            style={{ fontSize: 17, padding: '16px 18px' }}
          >
            English
          </button>
          <button
            type="button"
            className="auth-primary"
            onClick={() => onPick('zh')}
            style={{
              fontSize: 17,
              padding: '16px 18px',
              background: 'var(--surface)',
              color: 'var(--fg)',
              border: '0.5px solid var(--border)',
            }}
          >
            繁體中文
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared chrome ───────────────────────────────────────────────────────────
// `onClose` is passed only when the shell is rendered inside the auth-overlay
// (Sign in / Sign up / Forgot path opened from a CTA). In that case we expose
// a left-aligned "← Back" button that closes the overlay and returns the user
// to whatever was underneath (almost always the guest home screen).
//
// Deep-link recovery screens (ResetPassword / RecoveryLinkError / PostReset)
// are rendered WITHOUT onClose because they own the bootstrap state — they
// shouldn't be dismissable, only completable via their own primary buttons.
function AuthShell({ children, lang, setLang, onClose }) {
  return (
    <div className="auth-shell">
      <div className="auth-top">
        <div className="auth-top-left">
          {onClose && (
            <button type="button" className="auth-top-back" onClick={onClose}
                    aria-label={lang === 'zh' ? '返回首頁' : 'Back to home'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   aria-hidden="true">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              <span>{lang === 'zh' ? '返回' : 'Back'}</span>
            </button>
          )}
          <VitalBrand markSize={36} />
        </div>
        <LangPill lang={lang} setLang={setLang} />
      </div>
      <div className="auth-body">{children}</div>
      <div className="auth-foot mono muted-2">
        {lang === 'zh' ? '所有資料於本地及 Supabase 安全儲存' : 'Secured via Supabase · Hong Kong NCHK aligned'}
      </div>
    </div>
  );
}

function AuthField({ label, type='text', value, onChange, placeholder, autoFocus, autoComplete, inputRef, lang, min, max }) {
  // Date fields route through the wheel picker (<DateField>, components.jsx)
  // rather than a native <input type=date>. Rendered without the <label>
  // wrapper so a tap on the label text doesn't double-activate the trigger
  // button. Pass `lang` for the localized column labels / formatted value.
  if (type === 'date') {
    return (
      <div className="auth-field">
        <div className="auth-label mono">{label}</div>
        <DateField className="auth-input" value={value} onChange={onChange}
          lang={lang} min={min} max={max} placeholder={placeholder} />
      </div>
    );
  }
  return (
    <label className="auth-field">
      <div className="auth-label mono">{label}</div>
      <input
        ref={inputRef}
        className="auth-input"
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
      />
    </label>
  );
}

function AuthError({ msg, lang }) {
  if (!msg) return null;
  // friendlier copy for common errors
  let pretty = msg;
  const m = msg.toLowerCase();
  if (m.includes('invalid login'))         pretty = lang === 'zh' ? '電郵或密碼不正確' : 'Email or password is incorrect';
  else if (m.includes('already registered')) pretty = lang === 'zh' ? '此電郵已註冊 — 請登入' : 'Already registered — please sign in';
  else if (m.includes('email not confirmed')) pretty = lang === 'zh' ? '請先到電郵驗證帳戶' : 'Please confirm your email first';
  else if (m.includes('password should be')) pretty = lang === 'zh' ? '密碼最少 6 位' : 'Password must be at least 6 characters';
  // WebKit reports a failed fetch() as "Load failed"; Chromium as "Failed to
  // fetch". supabase-js wraps these (AuthRetryableFetchError) preserving the
  // original message. Show a connectivity message instead of the raw string.
  else if (m.includes('load failed') || m.includes('failed to fetch') || m.includes('network'))
    pretty = lang === 'zh' ? '連線伺服器失敗,請檢查網絡後再試一次。' : "Couldn't reach the server. Check your connection and try again.";
  return <div className="auth-error">{pretty}</div>;
}

// Avatar initials for the returning-user card. CJK names read best as a single
// glyph (the surname), so we don't try to abbreviate them like Latin names.
// Array.from keeps us safe across surrogate-pair characters (CJK ext-B etc.).
function avatarInitials(name, emailLocal) {
  const src = (name && name.trim()) || emailLocal || '';
  if (!src) return '?';
  // Kana + CJK (incl. ext-A and compatibility ideographs) → first glyph.
  if (/[぀-ヿ㐀-䶿一-鿿豈-﫿]/.test(src)) {
    return Array.from(src)[0];
  }
  const parts = src.split(/\s+/).filter(Boolean);
  const s = parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]) : src.slice(0, 2);
  return s.toUpperCase();
}

// ── Sign in ─────────────────────────────────────────────────────────────────
function SignInScreen({ lang, setLang, goSignUp, goForgot, onClose }) {
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [siwaBusy, setSiwaBusy] = React.useState(false);
  const [googleBusy, setGoogleBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  // Tracks which method the user signed in with most recently so we
  // can render a "Last used" hint next to that option. Read on mount;
  // doesn't update during this render because the value can only
  // change after a successful sign-in, which navigates away.
  const [lastMethod, setLastMethod] = React.useState(null);
  // Remembered email user for the returning-user quick-login card. Read once on
  // mount (same rationale as lastMethod: it can only change after a successful
  // sign-in, which navigates away from this screen).
  const [identity, setIdentity] = React.useState(null);
  const passwordRef = React.useRef(null);
  React.useEffect(() => {
    try {
      if (typeof AuthAPI?.getLastLoginMethod === 'function') {
        setLastMethod(AuthAPI.getLastLoginMethod());
      }
      if (typeof AuthAPI?.getLastIdentity === 'function') {
        setIdentity(AuthAPI.getLastIdentity());
      }
    } catch (_) {}
  }, []);

  const lastUsedLabel = lang === 'zh' ? '上次使用' : 'Last used';
  const pillStyle = {
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: 999,
    fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
    background: 'rgba(255,255,255,0.18)', color: '#fff',
  };
  const pillStyleDark = {
    ...pillStyle,
    background: 'var(--primary-soft, rgba(0,0,0,0.08))',
    color: 'var(--primary, #1a5f5b)',
  };

  // Returning-user card: only for users whose last login was email AND for whom
  // we have a remembered identity. Apple/Google users keep their own "Last used" pill.
  const showReturning = lastMethod === 'email' && !!identity?.email;
  const emailLocal = identity?.email ? identity.email.split('@')[0] : '';
  const returningName = (identity?.name && identity.name.trim())
    ? identity.name.trim()
    : (emailLocal ? emailLocal.charAt(0).toUpperCase() + emailLocal.slice(1) : '');
  const useReturning = () => {
    if (!identity?.email) return;
    setEmail(identity.email);
    // Defer focus until after React commits the email state change — a
    // synchronous focus() can fire before the DOM settles and silently no-op /
    // fail to raise the keyboard in the iOS WKWebView.
    requestAnimationFrame(() => passwordRef.current?.focus());
  };
  const forgetReturning = () => {
    try { AuthAPI.clearLastIdentity?.(); } catch (_) {}
    setIdentity(null);
    setEmail('');
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return; // guard the Enter-key path — the button is disabled but form submit still fires
    if (!email || !pw) return;
    setBusy(true); setErr('');
    const { error } = await AuthAPI.signIn(email.trim(), pw);
    setBusy(false);
    if (error) setErr(error.message);
    // success: auth state listener in app.jsx will swap the route
  };

  const submitApple = async () => {
    if (siwaBusy) return;
    setSiwaBusy(true); setErr('');
    const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
    const { error } = await AuthAPI.signInWithApple();
    if (error) {
      setSiwaBusy(false);
      // A user-initiated cancel isn't a failure — don't flash a red
      // error row at them. Surface only real errors (network, config,
      // missing token, etc.).
      const msg = (error.message || '').toLowerCase();
      if (!msg.includes('cancel')) setErr(error.message);
      return;
    }
    if (isNative) {
      // Native: signInWithApple resolved successfully; the auth listener
      // in app.jsx will swap the route shortly. Resetting here is safe
      // — there's no full-page redirect to mask, so leaving the spinner
      // up would just look stuck if the listener has any latency.
      setSiwaBusy(false);
    }
    // Web success path: keep siwaBusy true; the page is about to
    // redirect to Apple via Supabase's OAuth flow. The pageshow handler
    // below resets the state if the user comes back via bfcache (e.g.,
    // they cancelled on Apple's page and tapped browser-back).
  };

  const submitGoogle = async () => {
    if (googleBusy) return;
    setGoogleBusy(true); setErr('');
    const isNative = !!globalThis.Capacitor?.isNativePlatform?.();
    const { error } = await AuthAPI.signInWithGoogle();
    if (error) {
      setGoogleBusy(false);
      // A user-initiated cancel isn't a failure — don't flash a red error row.
      const msg = (error.message || '').toLowerCase();
      if (!msg.includes('cancel')) setErr(error.message);
      return;
    }
    if (isNative) {
      // Native: signInWithGoogle resolved successfully; the auth listener
      // in app.jsx will swap the route shortly.
      setGoogleBusy(false);
    }
    // Web success path: keep googleBusy true; the page is about to
    // redirect to Google via Supabase's OAuth flow.
  };

  // Clear the Apple/Google spinner whenever the user returns to this screen
  // with the sign-in still pending. The native Apple plugin can fail to settle
  // its promise when the user cancels the sheet — its delegate silently
  // bails when the saved call can't be found, so neither resolve nor reject
  // ever fires (see @capacitor-community/apple-sign-in Plugin.swift). That
  // would otherwise strand the button on "Signing in…" and, because the
  // email button is disabled while siwaBusy/googleBusy, lock the whole screen.
  // The same signals also cover a web user backing out of an OAuth page.
  // Being back on this screen always means the spinner is stale: a success
  // would have routed us to home instead.
  React.useEffect(() => {
    if (!siwaBusy && !googleBusy) return;
    const clear = () => { setSiwaBusy(false); setGoogleBusy(false); };
    const onPageShow = (e) => { if (e.persisted) clear(); };
    const onVisible = () => { if (document.visibilityState === 'visible') clear(); };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);

    // Native: dismissing the Apple/Google sheet brings the app back to the
    // foreground — the one signal we get when the plugin promise hangs.
    let appHandle, removed = false;
    const CapApp = globalThis.CapacitorApp;
    if (CapApp?.addListener) {
      (async () => {
        try {
          appHandle = await CapApp.addListener('appStateChange', (s) => { if (s?.isActive) clear(); });
          if (removed) appHandle.remove();
        } catch (_) {}
      })();
    }
    return () => {
      removed = true;
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
      if (appHandle) appHandle.remove();
    };
  }, [siwaBusy, googleBusy]);

  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '登入' : 'Sign in'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '歡迎回來。' : 'Welcome back.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh' ? '繼續追蹤你的 CNE 進度。' : 'Pick up where you left off — your CNE points and saved courses are waiting.'}
      </p>

      {showReturning ? (
        <div className="auth-returning">
          <button
            type="button"
            className="auth-returning-card"
            onClick={useReturning}
            aria-label={lang === 'zh' ? `以 ${returningName} 繼續登入` : `Continue as ${returningName}`}
          >
            <span className="auth-returning-avatar" aria-hidden="true">
              {avatarInitials(identity.name, emailLocal)}
            </span>
            <span className="auth-returning-meta">
              <span className="auth-returning-name">{returningName}</span>
              <span className="auth-returning-email">{identity.email}</span>
            </span>
            <svg className="auth-returning-chevron" width="16" height="16" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                 strokeLinejoin="round" aria-hidden="true">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <button type="button" className="auth-returning-switch" onClick={forgetReturning}>
            {lang === 'zh' ? '使用其他帳戶' : 'Use a different account'}
          </button>
        </div>
      ) : null}

      {/* Sign in with Apple — placed ABOVE email/password per Apple HIG. */}
      <div style={{ marginTop: 8, marginBottom: 8 }}>
          <button
            type="button"
            onClick={submitApple}
            disabled={siwaBusy}
            aria-label="Sign in with Apple"
            style={{
              width: '100%',
              minHeight: 44,
              padding: '11px 16px',
              background: '#000',
              color: '#fff',
              border: 0,
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
              opacity: siwaBusy ? 0.7 : 1,
            }}
          >
            {siwaBusy ? (
              <span
                aria-hidden="true"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: '#fff',
                  animation: 'vital-spin 0.7s linear infinite',
                }}
              />
            ) : (
              <svg width="16" height="20" viewBox="0 0 16 20" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M13.07 10.62c-.02-2.21 1.8-3.27 1.88-3.32-1.03-1.5-2.62-1.71-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.18-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.73 2.21 1.1-.04 1.51-.71 2.84-.71 1.33 0 1.7.71 2.86.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.31-.88-2.34-3.51zM10.86 4.16c.6-.74 1.01-1.75.9-2.76-.87.04-1.93.58-2.55 1.3-.55.64-1.04 1.68-.91 2.67.97.07 1.96-.49 2.56-1.21z"
                />
              </svg>
            )}
            <span>
              {siwaBusy
                ? (lang === 'zh' ? '登入中…' : 'Signing in…')
                : (lang === 'zh' ? '以 Apple 登入' : 'Sign in with Apple')}
            </span>
            {lastMethod === 'apple' && !siwaBusy ? (
              <span style={pillStyle}>{lastUsedLabel}</span>
            ) : null}
          </button>

          {/* Sign in with Google */}
          <button
            type="button"
            onClick={submitGoogle}
            disabled={googleBusy}
            aria-label="Sign in with Google"
            style={{
              width: '100%',
              minHeight: 44,
              padding: '11px 16px',
              marginTop: 8,
              background: '#fff',
              color: '#1f1f1f',
              border: '1px solid #dadce0',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: 'pointer',
              opacity: googleBusy ? 0.7 : 1,
            }}
          >
            {googleBusy ? (
              <span
                aria-hidden="true"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '2px solid rgba(0,0,0,0.12)',
                  borderTopColor: '#4285F4',
                  animation: 'vital-spin 0.7s linear infinite',
                }}
              />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            <span>
              {googleBusy
                ? (lang === 'zh' ? '登入中…' : 'Signing in…')
                : (lang === 'zh' ? '以 Google 登入' : 'Sign in with Google')}
            </span>
            {lastMethod === 'google' && !googleBusy ? (
              <span style={pillStyleDark}>{lastUsedLabel}</span>
            ) : null}
          </button>

          {/* Divider between SSO and email/password */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            margin: '14px 0 6px', color: 'var(--fg-3)', fontSize: 11,
            letterSpacing: '0.12em', textTransform: 'uppercase',
          }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span>{lang === 'zh' ? '或' : 'or'}</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
        </div>

      <form onSubmit={submit} className="auth-form">
        {/* No autoFocus on email: the iOS keyboard popping up the
            moment the screen lands hides the "Sign in with Apple"
            button below it. Users who want to type still get focus
            on the first tap. */}
        <AuthField label={lang === 'zh' ? '電郵' : 'Email'} type="email"
          value={email} onChange={setEmail} autoComplete="email"
          placeholder="you@hospital.hk" />
        <AuthField label={lang === 'zh' ? '密碼' : 'Password'} type="password"
          value={pw} onChange={setPw} autoComplete="current-password"
          placeholder="••••••••" inputRef={passwordRef} />
        <AuthError msg={err} lang={lang} />
        <button className="auth-primary" type="submit" disabled={busy || siwaBusy || googleBusy}
          aria-busy={busy || undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy ? <Spinner size={15} color="currentColor" /> : null}
          <span>{busy ? (lang === 'zh' ? '登入中…' : 'Signing in…') : (lang === 'zh' ? '登入' : 'Sign in')}</span>
          {lastMethod === 'email' && !busy && !showReturning ? (
            <span style={pillStyleDark}>{lastUsedLabel}</span>
          ) : null}
        </button>
      </form>

      <div className="auth-links">
        <button className="auth-link" type="button" onClick={goForgot}>
          {lang === 'zh' ? '忘記密碼？' : 'Forgot password?'}
        </button>
        <span className="muted-2" style={{ fontSize: 12 }}>·</span>
        <button className="auth-link" type="button" onClick={goSignUp}>
          {lang === 'zh' ? '建立帳戶' : 'Create account'}
        </button>
      </div>
    </AuthShell>
  );
}

// ── Sign up ─────────────────────────────────────────────────────────────────
function SignUpScreen({ lang, setLang, goSignIn, onClose }) {
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [pw2, setPw2] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return; // guard the Enter-key double-submit path
    if (!email || !pw) return;
    if (pw.length < 6)  return setErr(lang === 'zh' ? '密碼最少 6 位' : 'Password must be at least 6 characters');
    if (pw !== pw2)     return setErr(lang === 'zh' ? '兩次輸入的密碼不一致' : "Passwords don't match");
    setBusy(true); setErr('');
    const { data, error } = await AuthAPI.signUp(email.trim(), pw);
    setBusy(false);
    if (error) return setErr(error.message);
    // Supabase by default returns a session for confirmed-via-link projects;
    // if email confirmations are on, no session is returned — show "check inbox".
    if (data?.session) {
      // session listener will route us forward
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
        <div className="auth-eyebrow mono">{lang === 'zh' ? '請查看電郵' : 'Check your inbox'}</div>
        <h1 className="auth-title serif">
          {lang === 'zh' ? '差一步。' : 'One more step.'}
        </h1>
        <p className="auth-sub">
          {lang === 'zh'
            ? `我們已寄送驗證連結至 ${email}。請點擊連結完成註冊。`
            : `We've emailed a confirmation link to ${email}. Click it to finish setting up your account.`}
        </p>
        <p className="auth-sub muted-2" style={{ fontSize: 13, marginTop: 8 }}>
          {lang === 'zh'
            ? '收不到電郵？請檢查垃圾郵件箱。'
            : "Don't see it? Check your spam/junk folder."}
        </p>
        <button className="auth-primary" onClick={goSignIn} style={{ marginTop: 24 }}>
          {lang === 'zh' ? '返回登入' : 'Back to sign in'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '建立帳戶' : 'Create account'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '由今天開始。' : 'Start today.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh' ? '由 NCHK 認可的 13 間機構，118 個課程。' : '118 NCHK-accredited courses from 13 providers — in one place.'}
      </p>

      <form onSubmit={submit} className="auth-form">
        <AuthField label={lang === 'zh' ? '電郵' : 'Email'} type="email"
          value={email} onChange={setEmail} autoFocus autoComplete="email"
          placeholder="you@hospital.hk" />
        <AuthField label={lang === 'zh' ? '密碼' : 'Password'} type="password"
          value={pw} onChange={setPw} autoComplete="new-password"
          placeholder={lang === 'zh' ? '至少 6 位' : 'At least 6 characters'} />
        <AuthField label={lang === 'zh' ? '確認密碼' : 'Confirm password'} type="password"
          value={pw2} onChange={setPw2} autoComplete="new-password"
          placeholder="••••••••" />
        <AuthError msg={err} lang={lang} />
        <button className="auth-primary" type="submit" disabled={busy}
          aria-busy={busy || undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy ? <Spinner size={15} color="currentColor" /> : null}
          <span>{busy ? (lang === 'zh' ? '建立中…' : 'Creating…') : (lang === 'zh' ? '建立帳戶' : 'Create account')}</span>
        </button>
      </form>

      <div className="auth-links">
        <span className="muted-2" style={{ fontSize: 13 }}>
          {lang === 'zh' ? '已有帳戶？' : 'Already have an account?'}
        </span>
        <button className="auth-link" type="button" onClick={goSignIn}>
          {lang === 'zh' ? '登入' : 'Sign in'}
        </button>
      </div>
    </AuthShell>
  );
}

// ── Forgot / reset password ────────────────────────────────────────────────
function ForgotScreen({ lang, setLang, goSignIn, onClose }) {
  const [email, setEmail] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return; // guard the Enter-key double-submit path
    if (!email) return;
    setBusy(true); setErr('');
    const { error } = await AuthAPI.resetPassword(email.trim());
    setBusy(false);
    if (error) return setErr(error.message);
    setSent(true);
  };

  if (sent) {
    return (
      <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
        <div className="auth-eyebrow mono">{lang === 'zh' ? '已處理' : 'Sent'}</div>
        <h1 className="auth-title serif">
          {lang === 'zh' ? '查看電郵。' : 'Check your inbox.'}
        </h1>
        {/* Wording is intentionally generic — confirming that the email is
            (or isn't) registered would leak account existence. */}
        <p className="auth-sub">
          {lang === 'zh'
            ? '若此電郵已註冊，我們已寄出密碼重設電郵。'
            : "If this email is registered, we've sent a password reset email."}
        </p>
        <p className="auth-sub muted-2" style={{ fontSize: 13, marginTop: 8 }}>
          {lang === 'zh'
            ? '收不到電郵？請檢查垃圾郵件箱。'
            : "Don't see it? Check your spam/junk folder."}
        </p>
        <button className="auth-primary" onClick={goSignIn} style={{ marginTop: 24 }}>
          {lang === 'zh' ? '返回登入' : 'Back to sign in'}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '重設密碼' : 'Reset password'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '沒問題。' : "It happens."}
      </h1>
      <p className="auth-sub">
        {lang === 'zh' ? '輸入電郵，我們會寄出重設連結。' : "Enter your email and we'll send you a reset link."}
      </p>

      <form onSubmit={submit} className="auth-form">
        <AuthField label={lang === 'zh' ? '電郵' : 'Email'} type="email"
          value={email} onChange={setEmail} autoFocus autoComplete="email"
          placeholder="you@hospital.hk" />
        <AuthError msg={err} lang={lang} />
        <button className="auth-primary" type="submit" disabled={busy}
          aria-busy={busy || undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy ? <Spinner size={15} color="currentColor" /> : null}
          <span>{busy ? (lang === 'zh' ? '處理中…' : 'Sending…') : (lang === 'zh' ? '寄出重設連結' : 'Send reset link')}</span>
        </button>
      </form>

      <div className="auth-links">
        <button className="auth-link" type="button" onClick={goSignIn}>
          ← {lang === 'zh' ? '返回登入' : 'Back to sign in'}
        </button>
      </div>
    </AuthShell>
  );
}

// Reset-password-from-link landing (when user clicks recovery email)
function ResetPasswordScreen({ lang, setLang, onDone, onClose }) {
  const [pw, setPw] = React.useState('');
  const [pw2, setPw2] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return; // guard the Enter-key double-submit path
    if (pw.length < 6) return setErr(lang === 'zh' ? '密碼最少 6 位' : 'Password must be at least 6 characters');
    if (pw !== pw2)    return setErr(lang === 'zh' ? '兩次輸入的密碼不一致' : "Passwords don't match");
    setBusy(true); setErr('');
    const { error } = await sb.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) return setErr(error.message);
    onDone();
  };

  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '設定新密碼' : 'Set new password'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '差不多了。' : 'Almost done.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh' ? '輸入新密碼以繼續。' : 'Pick a new password to continue.'}
      </p>
      <form onSubmit={submit} className="auth-form">
        <AuthField label={lang === 'zh' ? '新密碼' : 'New password'} type="password"
          value={pw} onChange={setPw} autoFocus autoComplete="new-password" />
        <AuthField label={lang === 'zh' ? '確認密碼' : 'Confirm password'} type="password"
          value={pw2} onChange={setPw2} autoComplete="new-password" />
        <AuthError msg={err} lang={lang} />
        <button className="auth-primary" type="submit" disabled={busy}
          aria-busy={busy || undefined}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {busy ? <Spinner size={15} color="currentColor" /> : null}
          <span>{busy ? (lang === 'zh' ? '處理中…' : 'Saving…') : (lang === 'zh' ? '儲存並登入' : 'Save and continue')}</span>
        </button>
      </form>
    </AuthShell>
  );
}

// ── Locked screen ──────────────────────────────────────────────────────────
// Shown when biometric is enrolled and the user has not yet passed Face ID
// on this launch. The persisted Supabase session is held back from the UI
// until biometric succeeds — onRetry() owns the unlock flow end-to-end and
// returns { session } on success or { error, escape } on failure. An
// `escape: true` result means the stored credential is dead (revoked /
// expired / wrong account) and we must drop to the normal sign-in screen.
function LockedScreen({ lang, setLang, onRetry, onEscape }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [autoTried, setAutoTried] = React.useState(false);

  const tryAgain = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    let res;
    try { res = await onRetry({ source: 'locked-screen' }); }
    catch (e) { res = { error: { message: e?.message || 'Unknown error' } }; }
    setBusy(false);
    if (!res) return;
    if (res.session) return; // parent routes us away on success
    if (res.error?.message) setErr(res.error.message);
    if (res.escape) {
      // Dead credential — surface the message briefly, then drop to
      // sign-in. The caller already cleared the Keychain entry.
      setTimeout(() => { onEscape(); }, 600);
    }
  };

  // Auto-prompt Face ID once on mount — matches iOS native pattern
  // (banking apps, password managers). A small delay lets the locked
  // UI paint before the system sheet covers it. Failures are surfaced
  // by the shared tryAgain handler, so the "fire-and-forget" race in
  // the previous bootstrap implementation can't drop escape results.
  React.useEffect(() => {
    if (autoTried) return;
    setAutoTried(true);
    const t = setTimeout(() => { tryAgain(); }, 150);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthShell lang={lang} setLang={setLang}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '已鎖定' : 'Locked'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? 'Vital 已鎖定。' : 'Vital is locked.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '請使用 Face ID 解鎖,或改用其他方式登入。'
          : 'Unlock with Face ID, or sign in another way.'}
      </p>
      <AuthError msg={err} lang={lang} />
      <button
        className="auth-primary"
        onClick={tryAgain}
        disabled={busy}
        aria-busy={busy || undefined}
        style={{ marginTop: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        {busy ? <Spinner size={15} color="currentColor" /> : null}
        <span>{busy
          ? (lang === 'zh' ? '驗證中…' : 'Verifying…')
          : (lang === 'zh' ? '嘗試 Face ID' : 'Try Face ID')}</span>
      </button>
      <button
        className="auth-link"
        type="button"
        onClick={onEscape}
        disabled={busy}
        style={{ marginTop: 16 }}
      >
        {lang === 'zh' ? '改用其他方式登入' : 'Sign in another way'}
      </button>
    </AuthShell>
  );
}

// ── Post-reset confirmation ────────────────────────────────────────────────
// Shown after a successful password update via the recovery flow. The
// recovery session has already been signed out by the caller; this screen
// only exists to confirm the change and prompt the user to sign in fresh
// with the new password — recovery links never become auto-logins.
function PasswordResetSuccessScreen({ lang, setLang, onDone, onClose }) {
  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '已更新' : 'Updated'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '密碼已更新。' : 'Password updated.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '請以新密碼重新登入。'
          : 'Please sign in with your new password.'}
      </p>
      <button className="auth-primary" onClick={onDone} style={{ marginTop: 24 }}>
        {lang === 'zh' ? '前往登入' : 'Sign in'}
      </button>
    </AuthShell>
  );
}

// ── Recovery-link error landing ────────────────────────────────────────────
// Shown when a vital://reset URL arrives with `error=` / `error_code=` /
// `error_description=` instead of valid recovery tokens — typically an
// expired one-time link, a tampered URL, or a malformed redirect. The home
// screen is intentionally NEVER the fallback for this case.
function RecoveryLinkErrorScreen({ lang, setLang, error, onDone, onClose }) {
  const code = error?.code || '';
  const description = error?.description || '';
  // Heuristic: Supabase typically returns access_denied + otp_expired for
  // an expired recovery link. Anything else we treat as generic invalid.
  const expired = /expired|otp_expired/i.test(code) || /expired/i.test(description);

  const title = lang === 'zh'
    ? (expired ? '此連結已失效。' : '無法開啟連結。')
    : (expired ? 'This link has expired.' : 'Could not open this link.');
  const sub = lang === 'zh'
    ? '請重新申請密碼重設電郵，或返回登入。'
    : 'Please request a new password reset email, or sign in again.';

  return (
    <AuthShell lang={lang} setLang={setLang} onClose={onClose}>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '連結無效' : 'Link issue'}</div>
      <h1 className="auth-title serif">{title}</h1>
      <p className="auth-sub">{sub}</p>
      {description ? (
        <p className="muted-2" style={{ fontSize: 12, marginTop: 8 }}>{description}</p>
      ) : null}
      <button className="auth-primary" onClick={onDone} style={{ marginTop: 24 }}>
        {lang === 'zh' ? '返回登入' : 'Back to sign in'}
      </button>
    </AuthShell>
  );
}

// ── Auth router ─────────────────────────────────────────────────────────────
function AuthRouter({ lang, setLang, initialMode = 'signin', onClose }) {
  const [mode, setMode] = React.useState(initialMode);
  const shellProps = { lang, setLang, onClose };
  if (mode === 'signin')  return <SignInScreen {...shellProps} goSignUp={() => setMode('signup')} goForgot={() => setMode('forgot')} />;
  if (mode === 'signup')  return <SignUpScreen {...shellProps} goSignIn={() => setMode('signin')} />;
  if (mode === 'forgot')  return <ForgotScreen {...shellProps} goSignIn={() => setMode('signin')} />;
  if (mode === 'reset')   return <ResetPasswordScreen {...shellProps} onDone={() => setMode('signin')} />;
  return null;
}

Object.assign(window, { AuthRouter, SignInScreen, SignUpScreen, ForgotScreen, ResetPasswordScreen, RecoveryLinkErrorScreen, PasswordResetSuccessScreen, LockedScreen, LanguagePickerScreen });

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { LanguagePickerScreen, AuthShell, AuthField, AuthError, avatarInitials, SignInScreen, SignUpScreen, ForgotScreen, ResetPasswordScreen, LockedScreen, PasswordResetSuccessScreen, RecoveryLinkErrorScreen, AuthRouter });
